import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { NAV } from '@/constants/testIds';
import { LOGO_URL, STAN_STRATEGY_URL } from '@/constants/content';

const links = [
  { to: '/', label: 'Home', tid: NAV.home },
  { to: '/about', label: 'About', tid: NAV.about },
  { to: '/companies', label: 'Companies', tid: NAV.companies },
  { to: '/speaking', label: 'Speaking', tid: NAV.speaking },
  { to: '/media', label: 'Media', tid: NAV.media },
  { to: '/resources', label: 'Resources', tid: NAV.resources },
  { to: '/work-with-me', label: 'Work With Me', tid: NAV.workWithMe },
  { to: '/contact', label: 'Contact', tid: NAV.contact },
];

export default function Navigation() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid={NAV.root}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-ivory/85 backdrop-blur-xl border-b border-taupe/20' : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" data-testid={NAV.logo} className="flex items-center gap-3 group">
            <img src={LOGO_URL} alt="Toia Wallace" className="h-9 lg:h-11 w-auto object-contain" />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.slice(1, 7).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.tid}
                className={({ isActive }) =>
                  `font-label text-[11px] tracking-widest uppercase transition-colors duration-300 ${
                    isActive ? 'text-matte' : 'text-matte/60 hover:text-matte'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <NavLink to="/contact" data-testid={NAV.contact}
              className={({ isActive }) =>
                `font-label text-[11px] tracking-widest uppercase transition-colors duration-300 ${
                  isActive ? 'text-matte' : 'text-matte/60 hover:text-matte'
                }`
              }>Contact</NavLink>
          </nav>

          <div className="hidden lg:flex items-center gap-5">
            <NavLink
              to="/founders-list"
              data-testid="nav-founders-list"
              className={({ isActive }) =>
                `font-label text-[10px] tracking-widest uppercase transition-colors duration-300 ${
                  isActive ? 'text-gold' : 'text-matte/70 hover:text-gold'
                }`
              }
            >
              Join The List
            </NavLink>
            <a
              href={STAN_STRATEGY_URL}
              target="_blank"
              rel="noreferrer"
              data-testid={NAV.cta}
              className="btn-primary text-[10px]"
            >
              Book a Strategy Intensive
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </a>
          </div>

          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 -mr-2 text-matte"
            aria-label="Toggle navigation"
          >
            {open ? <X size={22} strokeWidth={1.4} /> : <Menu size={22} strokeWidth={1.4} />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      <div
        data-testid={NAV.mobileDrawer}
        className={`lg:hidden bg-ivory transition-[max-height,opacity] duration-500 overflow-hidden ${
          open ? 'max-h-[100vh] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-6 py-8 space-y-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`${l.tid}-mobile`}
              className={({ isActive }) =>
                `block font-serif-display text-3xl py-3 border-b border-taupe/20 ${
                  isActive ? 'text-matte' : 'text-matte/70'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={STAN_STRATEGY_URL}
            target="_blank"
            rel="noreferrer"
            data-testid={`${NAV.cta}-mobile`}
            className="btn-primary w-full justify-center mt-8"
          >
            Book a Strategy Intensive
          </a>
          <NavLink
            to="/founders-list"
            data-testid="nav-founders-list-mobile"
            className="inline-flex items-center justify-center gap-2 w-full mt-4 py-4 px-8 rounded-full border border-matte text-matte text-[0.72rem] font-semibold tracking-[0.28em] uppercase hover:text-gold hover:border-gold transition-colors"
          >
            Join The Founder&rsquo;s List
          </NavLink>
        </div>
      </div>
    </header>
  );
}
